import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listSorties, listSortiesCount } from '../../api/sorties';
import { listIssues, listIssuesCount } from '../../api/issues';
import { listRoomReportsCount } from '../../api/roomReports';
import { listStudents } from '../../api/students';
import { getCurrentSemestre } from '../../api/semestres';
import { listEvaluations } from '../../api/evaluations';
import { initNotificationSounds, playTestSound, playUrgentSound, playWarningSound } from '../../utils/notificationSounds';
import { isPageVisible } from '../../utils/pageVisibility';

const SoundIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
  </svg>
);

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const ITEMS = [
  { key: 'overdue',    label: 'sorties en retard',            statKey: 'overdue',       accentColor: '#e24b4a', path: '/supervisor/sorties?overdue=true',              priority: 'danger' },
  { key: 'pending',   label: "sorties en attente d'approbation", statKey: 'pending',    accentColor: '#faad14', path: '/supervisor/sorties?statut=PENDING',            priority: 'warning' },
  { key: 'issues',    label: 'signalements ouverts',          statKey: 'issuesOpen',    accentColor: '#faad14', path: '/supervisor/issues?statut=OPEN',                priority: 'warning' },
  { key: 'reports',   label: 'rapports à traiter',            statKey: 'pendingReports',accentColor: '#d48806', path: '/supervisor/reports?status=SIGNED_BY_STUDENT',  priority: 'warning' },
  { key: 'evaluations', label: 'évaluations',                 statKey: 'evaluations',   accentColor: '#3b82f6', path: '/supervisor/evaluations',                       priority: 'info' },
];

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ pending: 0, overdue: 0, issuesOpen: 0, issuesActive: 0, outStudents: 0, pendingReports: 0, evaluations: 0 });
  const [current, setCurrent] = useState(null);
  const prevStatsRef = useRef(null);
  const lastSoundAtRef = useRef(0);
  const alertedOverdueOnSessionRef = useRef(false);

  const maybePlaySounds = (prev, next) => {
    const now = Date.now();
    if (now - lastSoundAtRef.current < 12000) return;
    const overdueIncreased = Number(next?.overdue || 0) > Number(prev?.overdue || 0);
    const issuesIncreased = Number(next?.issuesOpen || 0) > Number(prev?.issuesOpen || 0);
    const reportsIncreased = Number(next?.pendingReports || 0) > Number(prev?.pendingReports || 0);
    if (overdueIncreased || issuesIncreased) { playUrgentSound(); lastSoundAtRef.current = now; return; }
    if (reportsIncreased) { playWarningSound(); lastSoundAtRef.current = now; }
  };

  useEffect(() => {
    initNotificationSounds();
    try { alertedOverdueOnSessionRef.current = sessionStorage.getItem('iugb_sound_overdue_alerted_v1') === '1'; }
    catch { alertedOverdueOnSessionRef.current = false; }
    let mounted = true;
    const refresh = async () => {
      try {
        const [sortiesRes, overdueRes, issues, issuesOpenRes, students, cur, reportsRes, evals] = await Promise.all([
          listSorties({ statut: 'PENDING', page: 1, pageSize: 1 }),
          listSortiesCount({ overdue: true }),
          listIssues({}),
          listIssuesCount({ statut_in: 'OPEN' }),
          listStudents({}),
          getCurrentSemestre(),
          listRoomReportsCount({ status: 'SIGNED_BY_STUDENT' }),
          listEvaluations({}),
        ]);
        const issuesActive = (issues || []).filter((i) => i.statut === 'OPEN' || i.statut === 'IN_PROGRESS').length;
        const issuesOpen = Number(issuesOpenRes?.total || 0);
        const outStudents = (students || []).filter((s) => s.statut === 'OUT').length;
        const nextStats = { pending: sortiesRes?.total || 0, overdue: Number(overdueRes?.total || 0), issuesOpen, issuesActive, outStudents, pendingReports: Number(reportsRes?.total || 0), evaluations: Array.isArray(evals) ? evals.length : 0 };
        if (mounted) {
          setCurrent(cur); setStats(nextStats);
          const visible = isPageVisible();
          if (
            visible &&
            !alertedOverdueOnSessionRef.current &&
            Number(nextStats?.overdue || 0) > 0
          ) {
            playUrgentSound(); alertedOverdueOnSessionRef.current = true;
            try { sessionStorage.setItem('iugb_sound_overdue_alerted_v1', '1'); } catch { /* ignore */ }
          }
          if (visible && prevStatsRef.current) maybePlaySounds(prevStatsRef.current, nextStats);
          prevStatsRef.current = nextStats;
        }
      } catch { /* ignore */ }
    };
    refresh();
    const id = setInterval(() => {
      if (isPageVisible()) refresh();
    }, 45000);
    const onVis = () => {
      if (isPageVisible()) refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      mounted = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statCards = [
    { label: 'Sorties en retard',   value: stats.overdue,         color: stats.overdue > 0 ? '#e24b4a' : undefined },
    { label: 'Sorties en attente',  value: stats.pending,         color: stats.pending > 0 ? '#faad14' : undefined },
    { label: 'Signalements OPEN',   value: stats.issuesOpen,      color: stats.issuesOpen > 0 ? '#faad14' : undefined },
    { label: 'Étudiants OUT',       value: stats.outStudents,     color: stats.outStudents > 0 ? '#3b82f6' : undefined },
    { label: 'Rapports à traiter',  value: stats.pendingReports,  color: stats.pendingReports > 0 ? '#d48806' : undefined },
    { label: 'Évaluations',         value: stats.evaluations,     color: stats.evaluations > 0 ? '#3b82f6' : undefined },
  ];

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div>
          <div className="section-label">Tableau de bord — Superviseur</div>
          <p style={{ margin: 0, fontSize: 12, color: '#888' }}>
            {current ? `Semestre actif: ${current.nom} ${current.annee}` : 'Aucun semestre actif'}
          </p>
        </div>
        <button className="btn-outline" onClick={() => playTestSound()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <SoundIcon /> Tester son
        </button>
      </div>

      {/* Stat cards */}
      <div className="stats-grid" style={{ marginTop: 16 }}>
        {statCards.map((s) => (
          <div className="stat-card" key={s.label}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={s.color ? { color: s.color } : {}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Actions required */}
      <div className="card" style={{ marginTop: 14 }}>
        <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>Actions requises aujourd'hui</span>
        </div>
        <div style={{ padding: '4px 0' }}>
          {ITEMS.map((it) => {
            const count = Number(stats[it.statKey] || 0);
            const disabled = count === 0 && it.key !== 'evaluations';
            return (
              <div key={it.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ display: 'inline-flex', minWidth: 28, height: 20, borderRadius: 10, background: `${it.accentColor}22`, color: it.accentColor, fontSize: 11, fontWeight: 700, alignItems: 'center', justifyContent: 'center', padding: '0 7px' }}>{count}</span>
                  <span style={{ fontSize: 13, color: '#1a1a1a' }}>{it.label}</span>
                </div>
                <button
                  className={it.priority === 'danger' && count > 0 ? 'btn-primary' : 'btn-outline'}
                  disabled={disabled}
                  onClick={() => navigate(it.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '0 12px' }}
                >
                  {it.key === 'evaluations' ? 'Voir' : 'Traiter'}
                  <ArrowIcon />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
